import { getAiConfig, getBaseUrl } from './config';

/** 文本内容部分（OpenAI vision 多模态格式） */
export interface ChatTextPart {
  type: 'text';
  text: string;
}

/** 图片内容部分（OpenAI vision 多模态格式） */
export interface ChatImagePart {
  type: 'image_url';
  image_url: { url: string };
}

export type ChatContentPart = ChatTextPart | ChatImagePart;

interface ChatRequestMessage {
  role: 'system' | 'user' | 'assistant';
  /** content 可以是字符串，或 OpenAI vision 数组格式（多模态） */
  content: string | ChatContentPart[];
}

// ============================================================================
// 9.3 API 用量记录
// 数据结构：{ [YYYY-MM]: { requests, totalTokens, promptTokens, completionTokens } }
// 存 localStorage key 'hump-ai-usage'，按月累计。
// ============================================================================

export interface AiUsageMonth {
  requests: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
}

export type AiUsageRecord = Record<string, AiUsageMonth>;

const USAGE_KEY = 'hump-ai-usage';

function currentMonthKey(): string {
  return new Date().toISOString().slice(0, 7); // YYYY-MM
}

/** 读取全部用量记录 */
export function getAiUsage(): AiUsageRecord {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AiUsageRecord;
  } catch {
    return {};
  }
}

/** 读取当月用量 */
export function getCurrentMonthUsage(): AiUsageMonth {
  const usage = getAiUsage();
  return (
    usage[currentMonthKey()] ?? {
      requests: 0,
      totalTokens: 0,
      promptTokens: 0,
      completionTokens: 0,
    }
  );
}

/** 累加一次调用的 token 用量 */
function recordUsage(
  promptTokens: number | undefined,
  completionTokens: number | undefined
): void {
  try {
    const prompt = promptTokens ?? 0;
    const completion = completionTokens ?? 0;
    const total = prompt + completion;
    if (total <= 0) return;

    const usage = getAiUsage();
    const key = currentMonthKey();
    const month = usage[key] ?? {
      requests: 0,
      totalTokens: 0,
      promptTokens: 0,
      completionTokens: 0,
    };
    month.requests += 1;
    month.totalTokens += total;
    month.promptTokens += prompt;
    month.completionTokens += completion;
    usage[key] = month;
    localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
  } catch {
    // 用量记录失败不影响主流程
  }
}

export async function chatCompletion(
  messages: ChatRequestMessage[],
  opts?: { model?: string; temperature?: number }
): Promise<string> {
  const config = await getAiConfig();
  if (!config || !config.apiKey) {
    throw new Error('AI 未配置，请先在设置中填写 API Key');
  }

  const baseUrl = getBaseUrl(config);
  const model = opts?.model || config.model;
  const temperature = opts?.temperature ?? 0.3;

  const response = await fetch(`${baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages,
      temperature,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    if (response.status === 401) throw new Error('API Key 无效，请检查设置');
    if (response.status === 429) throw new Error('请求过于频繁，请稍后重试');
    throw new Error(`AI 服务错误 (${response.status}): ${errorBody.slice(0, 200)}`);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content ?? '';

  // 9.3 记录 token 用量（OpenAI 兼容接口返回 usage 字段）
  const usage = data.usage;
  if (usage) {
    recordUsage(usage.prompt_tokens, usage.completion_tokens);
  } else {
    // 兜底：粗略估算（4 字符 ≈ 1 token），仍记一次请求
    // 支持字符串与多模态数组两种 content 格式
    const promptChars = messages.reduce((sum, m) => {
      if (typeof m.content === 'string') return sum + m.content.length;
      if (Array.isArray(m.content)) {
        return sum + m.content.reduce((s, p) => {
          if (p.type === 'text') return s + (p.text?.length ?? 0);
          if (p.type === 'image_url') return s + 768; // 图片估算为 ~768 token
          return s;
        }, 0);
      }
      return sum;
    }, 0);
    const completionChars = content.length;
    recordUsage(Math.ceil(promptChars / 4), Math.ceil(completionChars / 4));
  }

  return content;
}

export async function testConnection(): Promise<boolean> {
  try {
    const result = await chatCompletion(
      [{ role: 'user', content: 'Hi' }],
      { temperature: 0 }
    );
    return !!result;
  } catch {
    return false;
  }
}
